(ns ductile.ui.components.filters
  (:require [clojure.string :as str]
            [ductile.ui.components :as ui]
            [ductile.ui.locale :refer [tr]]))

(def operators
  [:exact :contains :matches :not-contains :not-matches :range :boolean])

(def operator->label
  {:exact (tr "Is")
   :contains (tr "Contains")
   :matches (tr "Matches")
   :not-contains (tr "Doesn't contain")
   :not-matches (tr "Doesn't match")
   :range (tr "Range")
   :boolean (tr "Is")})

(defn field-key [field]
  (if (vector? field) field [field]))

(defn normalize-field-specs [field-specs]
  (if (map? field-specs)
    field-specs
    (into {} (map (juxt ::field identity)) field-specs)))

(defn normalize-text [v]
  (-> (str (or v "")) str/trim str/lower-case))

(defn value-seq [v]
  (cond
    (nil? v) []
    (string? v) [v]
    (coll? v) (remove nil? v)
    :else [v]))

(defn extract-value [row {::keys [field extract]}]
  (if extract
    (extract row)
    (get-in row (field-key field))))

(defn glob->regex [glob]
  (try
    (as-> (str glob) g
      (str/lower-case g)
      (str/replace g #"[\\\^\$\.\+\(\)\{\}\|]" "\\$0")
      (str/replace g #"\*" ".*")
      (str/replace g #"\?" ".")
      (str "^" g "$")
      (js/RegExp. g))
    (catch :default _ nil)))

(defn parse-number [v]
  (let [n (js/parseFloat (str v))]
    (when-not (js/isNaN n) n)))

(defn parse-date-ms [v]
  (cond
    (instance? js/Date v) (let [d (js/Date. (.getTime ^js/Date v))]
                            (.setHours d 0 0 0 0)
                            (.getTime d))
    (string? v) (let [d (js/Date. v)]
                  (when-not (js/isNaN (.getTime d))
                    (.setHours d 0 0 0 0)
                    (.getTime d)))
    :else nil))

(defn value->comparable [field-type v]
  (case field-type
    :number (parse-number v)
    :date (parse-date-ms v)
    :text (normalize-text v)
    :set (normalize-text v)
    :boolean (boolean v)
    (normalize-text v)))

(defn label-for-filter [field-spec filter-item]
  (let [field-label (or (::label field-spec) (name (::field field-spec)))
        operator (::operator filter-item)
        value (::value filter-item)]
    (or (::label filter-item)
        (case operator
          :range (let [{:keys [from to]} (or value {})]
                   (str field-label ": "
                        (or from "") (when (and from to) " - ") (or to "")))
          :boolean (str field-label ": " (if value (tr "Yes") (tr "No")))
          :exact (str field-label ": " value)
          :contains (str field-label ": *" value "*")
          :matches (if (map? value)
                     (str field-label ": " (str/join ", " (:any-of value)))
                     (str field-label ": " value))
          :not-contains (str field-label ": !" value)
          :not-matches (str field-label ": !" value)
          (str field-label ": " value)))))

(defn ordered-operators [op-set]
  (let [allowed (or op-set (set operators))]
    (filterv allowed operators)))

(defn default-operator-for-spec [field-spec]
  (let [ops (or (::operators field-spec) #{})
        ordered (ordered-operators ops)
        field-type (::type field-spec)]
    (if (and (= :text field-type)
             (contains? ops :contains))
      :contains
      (first ordered))))

(defn compile-predicate [field-specs {:keys [::field ::operator ::value] :as filter-item}]
  (let [field-specs (normalize-field-specs field-specs)
        field-spec (get field-specs field)
        custom-predicate (::predicate field-spec)
        field-type (or (::type field-spec) :text)
        regex (when (and (#{:matches :not-matches} operator) (string? value))
                (glob->regex value))
        any-of (when (map? value)
                 (set (map normalize-text (:any-of value))))
        filter-value (if (map? value)
                       value
                       (value->comparable field-type value))
        {:keys [from to]} (when (= :range operator) (or value {}))
        from* (when (= :range operator) (value->comparable field-type from))
        to* (when (= :range operator) (value->comparable field-type to))]
    (if custom-predicate
      (custom-predicate filter-item)
      (fn [row]
        (let [raw (extract-value row field-spec)
              vals (value-seq raw)
              normalized (map #(value->comparable field-type %) vals)]
          (case operator
            :exact
            (if (seq any-of)
              (boolean (some any-of normalized))
              (boolean (some #(= % filter-value) normalized)))

            :contains
            (boolean (some #(some? (str/index-of (normalize-text %) filter-value)) normalized))

            :not-contains
            (or (empty? normalized)
                (not-any? #(some? (str/index-of (normalize-text %) filter-value)) normalized))

            :matches
            (cond
              (seq any-of) (boolean (some any-of normalized))
              regex (boolean (some #(re-matches regex (normalize-text %)) normalized))
              :else false)

            :not-matches
            (cond
              (seq any-of) (or (empty? normalized) (not-any? any-of normalized))
              regex (or (empty? normalized)
                        (not-any? #(re-matches regex (normalize-text %)) normalized))
              :else true)

            :range
            (boolean
             (some (fn [candidate]
                     (and (some? candidate)
                          (if (some? from*) (<= from* candidate) true)
                          (if (some? to*) (<= candidate to*) true)))
                   normalized))

            :boolean
            (= (boolean raw) (boolean value))

            true))))))

(defn apply-filters [rows field-specs active-filters]
  (if (seq active-filters)
    (let [preds (keep #(compile-predicate field-specs %) active-filters)]
      (if (seq preds)
        (filterv (apply every-pred preds) rows)
        rows))
    rows))

(defn serialize-filters [filters]
  (mapv (fn [f]
          (select-keys f [::id ::field ::operator ::value ::label]))
        filters))

(defn deserialize-filters [filters]
  (->> (or filters [])
       (keep (fn [f]
               (when (and (::field f) (::operator f))
                 (assoc f ::id (or (::id f) (random-uuid))))))
       vec))

(defn default-filter-item [field-specs]
  (let [field-spec (-> field-specs vals first)
        field (::field field-spec)]
    {::id (random-uuid)
     ::field field
     ::operator (default-operator-for-spec field-spec)
     ::value ""
     ::label nil}))

(defn apply-text->filter [field-specs filter-item]
  (let [field-specs (normalize-field-specs field-specs)
        field-spec (get field-specs (::field filter-item))
        parser (::text->filter field-spec)
        parsed (when parser (parser filter-item))]
    (cond
      (nil? parsed) [filter-item]
      (vector? parsed) parsed
      (map? parsed) [parsed]
      :else [filter-item])))

(defn validate-filter-item [field-specs {:keys [::field ::operator ::value]}]
  (let [field-spec (get (normalize-field-specs field-specs) field)
        field-type (or (::type field-spec) :text)]
    (case operator
      :matches
      (if (map? value)
        (if (seq (:any-of value)) nil (tr "No values"))
        (when-not (glob->regex value) (tr "Invalid glob pattern")))

      :range
      (let [{:keys [from to]} (or value {})
            from* (when (seq (str from)) (value->comparable field-type from))
            to* (when (seq (str to)) (value->comparable field-type to))]
        (cond
          (and (nil? from*) (nil? to*)) (tr "Enter from and/or to")
          (and (seq (str from)) (nil? from*)) (tr "Invalid from")
          (and (seq (str to)) (nil? to*)) (tr "Invalid to")
          (and from* to* (> from* to*)) (tr "From must be <= to")
          :else nil))

      nil)))

(defn row-choices [field-spec rows]
  (let [choices-fn (::choices-fn field-spec)]
    (when choices-fn
      (->> (choices-fn rows)
           (remove nil?)
           (map str)
           distinct
           sort
           vec))))

(defn field->testid-suffix [field]
  (-> (pr-str field)
      str/lower-case
      (str/replace #"[^a-z0-9]+" "-")
      (str/replace #"(^-+|-+$)" "")))

(defn input-for-row [{:keys [field-spec rows filter-item on-change value-testid]}]
  (let [{:keys [::operator ::value ::id]} filter-item
        field-type (::type field-spec)
        choices (row-choices field-spec rows)
        datalist-id (str "filters-list-" id)]
    (cond
      (= operator :boolean)
      [ui/select
       {:size 1
        :data-testid value-testid
        :value (boolean value)
        :on-change #(on-change (assoc filter-item ::value %))
        :choices [{:ui/value true :ui/label (tr "Yes")}
                  {:ui/value false :ui/label (tr "No")}]}]

      (= operator :range)
      [:div.flex.items-center.gap-1
       [:input.w-full.border.border-slate-300.rounded.px-2.py-1
        {:class "text-[12px]"
         :data-testid (str value-testid "-from")
         :type (if (= :date field-type) "date" "text")
         :placeholder (tr "From")
         :value (or (:from value) "")
         :on-change #(on-change (assoc filter-item ::value
                                       (assoc (if (map? value) value {}) :from (.. % -target -value))))}]
       [:span.text-slate-500 {:class "text-[12px]"} "–"]
       [:input.w-full.border.border-slate-300.rounded.px-2.py-1
        {:class "text-[12px]"
         :data-testid (str value-testid "-to")
         :type (if (= :date field-type) "date" "text")
         :placeholder (tr "To")
         :value (or (:to value) "")
         :on-change #(on-change (assoc filter-item ::value
                                       (assoc (if (map? value) value {}) :to (.. % -target -value))))}]]

      (and (= operator :exact) (seq choices))
      [ui/select
       {:size 1
        :data-testid value-testid
        :value (or value "")
        :on-change #(on-change (assoc filter-item ::value %))
        :choices (mapv (fn [choice]
                         {:ui/value choice
                          :ui/label choice})
                       choices)}]

      (and (= field-type :set) (seq choices))
      [ui/select
       {:size 1
        :data-testid value-testid
        :value (or value "")
        :on-change #(on-change (assoc filter-item ::value %))
        :choices (mapv (fn [choice]
                         {:ui/value choice
                          :ui/label choice})
                       choices)}]

      :else
      [:div
       [:input.w-full.border.border-slate-300.rounded.px-2.py-1
        {:class "text-[12px]"
         :data-testid value-testid
         :list (when (seq choices) datalist-id)
         :type (if (and (= operator :exact) (= :date field-type)) "date" "text")
         :value (or value "")
         :on-change #(on-change (assoc filter-item ::value (.. % -target -value)))}]
       (when (seq choices)
         [:datalist {:id datalist-id}
          (for [choice choices]
            ^{:key (str id "-" choice)}
            [:option {:value choice}])])])))

(defn finalize-filters [field-specs filters]
  (let [field-specs (normalize-field-specs field-specs)]
    (->> filters
         (mapcat #(apply-text->filter field-specs %))
         (map (fn [item]
                (let [spec (get field-specs (::field item))]
                  (assoc item ::label (label-for-filter spec item)))))
         vec)))

(defn normalize-field-key [field]
  (if (and (vector? field) (= 1 (count field)))
    (first field)
    field))

(defn filters-chip-bar [{:keys [filters on-remove on-open-modal on-reset-all empty-label highlight-field]}]
  [:div.flex.items-center.gap-1.flex-wrap.py-1
   {:class "text-[11px]"}
   (if (seq filters)
     (into [:<>]
           (map-indexed
            (fn [idx {:keys [::id ::label ::field]}]
              (let [highlighted? (= (normalize-field-key field) (normalize-field-key highlight-field))]
                [:div.inline-flex.items-center.gap-1.rounded-full.h-5.px-2.cursor-pointer
                 {:key (str "filter-chip-" id "-" idx)
                  :class (if highlighted?
                           "bg-[#6900ff] text-white hover:bg-[#852FFF]"
                           "bg-[#e6edfa] hover:bg-[#C8DAFD]")
                  :role "button"
                  :on-click on-open-modal}
                 [:span.font-medium label]
                 [:button
                  {:class (if highlighted?
                            "text-indigo-100 hover:text-white"
                            "text-slate-500 hover:text-slate-800")
                   :type "button"
                   :on-click (fn [e]
                               (.stopPropagation e)
                               (on-remove id))}
                  [ui/icon {:size 12} :cross-2]]]))
            filters))
     [:<>])
   [:button.inline-flex.items-center.justify-center.gap-1.rounded-full.h-5.px-2
    {:class "min-w-[45px] bg-[#e6edfa] hover:bg-[#C8DAFD]"
     :data-testid "filters-chip-add-filters"
     :type "button"
     :on-click on-open-modal}
    [ui/icon {:size 12} :plus]
    [:span (tr "Add filters…")]]
   (when-not (seq filters)
     [:span.ml-2.text-slate-500 (or empty-label (tr "No active filters"))])
   (when (and (seq filters) on-reset-all)
     [:button.bg-transparent.border-0.p-0.m-0
      {:class "ml-2 text-indigo-700 hover:text-indigo-800 underline underline-offset-2 decoration-slate-300 hover:decoration-indigo-500"
       :type "button"
       :on-click on-reset-all}
      (tr "Reset all")])])

(defn filters-modal [{:keys [field-specs rows filters on-change on-apply on-cancel on-reset title preview-render]}]
  (let [field-order (if (map? field-specs)
                      (keys field-specs)
                      (map ::field field-specs))
        field-specs (normalize-field-specs field-specs)
        field-options (map (fn [field]
                             {:ui/value field
                              :ui/label (get-in field-specs [field ::label])
                              :data-testid (str "filters-field-option-" (field->testid-suffix field))})
                           field-order)
        add-row! #(on-change (conj (vec filters) (default-filter-item field-specs)))
        update-row! (fn [row-id f]
                      (on-change
                       (mapv (fn [item]
                               (if (= row-id (::id item))
                                 (f item)
                                 item))
                             filters)))
        remove-row! (fn [row-id]
                      (on-change (vec (remove #(= row-id (::id %)) filters))))
        errors (into {}
                     (map (fn [item] [(::id item) (validate-filter-item field-specs item)]))
                     filters)
        impact-counts (into {}
                            (map (fn [item]
                                   [(::id item) (count (apply-filters rows field-specs [item]))]))
                            filters)
        can-apply? (every? nil? (vals errors))
        preview-filters (finalize-filters field-specs filters)
        preview-rows (apply-filters rows field-specs preview-filters)]
    [ui/modal {:on-close on-cancel
               :attrs {:class "max-w-[980px] w-[95vw] p-4"
                       :style {:top "100px"
                               :transform "translate(-50%, 0)"}}}
     [:div
      [:div.flex.items-center.justify-between.mb-3
       [:div.font-bold {:class "text-[14px]"} (or title (tr "Filters"))]]
      [:div.overflow-auto.space-y-2
       {:class "max-h-[60vh]"}
       (if (seq filters)
         (into
          [:<>]
          (for [item filters
                :let [field (::field item)
                      spec (get field-specs field)
                      operators' (ordered-operators (::operators spec))
                      fixed-filter? (true? (::fixed-filter? spec))
                      operator-options (map (fn [op]
                                              {:ui/value op :ui/label (get operator->label op (name op))})
                                            operators')
                      show-operator? (and (not fixed-filter?) (> (count operators') 1))
                      value-col-span (if show-operator? 4 7)
                      err (get errors (::id item))
                      impact (get impact-counts (::id item) 0)]]
            ^{:key (str "filter-row-" (::id item))}
            [:div.relative.border.border-slate-200.rounded.p-2.bg-slate-50
             [:button.text-slate-500.hover:text-slate-800.absolute.top-1.right-1
              {:type "button"
               :on-click #(remove-row! (::id item))}
              [ui/icon {:size 14} :cross-2]]
             [:div.grid.grid-cols-12.gap-2.items-center.pr-5
              [:div.col-span-4
               [ui/select
                {:size 1
                 :data-testid (str "filter-row-field-" (::id item))
                 :value field
                 :on-change (fn [new-field]
                              (let [new-spec (get field-specs new-field)]
                                (update-row! (::id item)
                                             (fn [_]
                                               {::id (::id item)
                                                ::field new-field
                                                ::operator (default-operator-for-spec new-spec)
                                                ::value (if (= :boolean (::type new-spec)) false "")
                                                ::label nil}))))
                 :choices field-options}]]
              (when show-operator?
                [:div.col-span-3
                 [ui/select
                  {:size 1
                   :data-testid (str "filter-row-operator-" (::id item))
                   :value (::operator item)
                   :on-change #(update-row! (::id item) (fn [f] (assoc f ::operator % ::label nil)))
                   :choices operator-options}]])
              [:div {:class (str "col-span-" value-col-span)}
               (if fixed-filter?
                 nil
                 [input-for-row {:field-spec spec
                                 :rows rows
                                 :filter-item item
                                 :value-testid (str "filter-row-value-" (::id item))
                                 :on-change #(update-row! (::id item) (fn [_] %))}])]]
             [:div.mt-1.text-slate-500 {:class "text-[11px]"}
              (tr "Affects %1 vehicles" impact)]
             (when err
               [:div.mt-1.text-red-700 {:class "text-[11px]"} err])]))
         [:div.text-slate-500 {:class "text-[12px]"} (tr "No filters configured")])]
      [:div.mt-3.flex.items-center.justify-between
       [ui/button {:size 0
                   :variant :white
                   :class "px-3 py-2"
                   :data-testid "filters-modal-add-filter"
                   :on-click add-row!}
        [ui/icon {:size 12} :plus]
        (tr "Add filter")]
       (when (seq filters)
         [:button.inline-flex.items-center.gap-1.text-slate-600.underline
          {:class "text-[12px]"
           :type "button"
           :on-click on-reset}
          (tr "Reset all filters")])]
      [:div.mt-3.h-64.border.border-slate-200.rounded.overflow-hidden
       (when preview-render
         [preview-render {:rows preview-rows
                          :count (count preview-rows)
                          :all-count (count rows)}])]
      [:div.mt-4.flex.items-center.justify-between
       [ui/button {:size 1 :variant :white :on-click on-cancel} (tr "Cancel")]
       [ui/button {:size 1
                   :variant :primary
                   :disabled? (not can-apply?)
                   :on-click (fn []
                               (let [finalized (finalize-filters field-specs filters)]
                                 (on-apply finalized)))} (tr "Apply filters")]]]]))
